import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { Building, Building2, Plus, X, MapPin, ArrowRight } from "lucide-react";

const CRITICITE = [["faible", "Faible"], ["moderee", "Modérée"], ["elevee", "Élevée"], ["critique", "Critique"]];
const ENT_TYPES = [["exterieure", "Entreprise extérieure"], ["soustraitant", "Sous-traitant"]];

function EmptyState({ icon: Icon, title, text, cta, onCta, testid }) {
  return (
    <div className="empty-state" data-testid={testid}>
      <Icon size={30} />
      <h3>{title}</h3>
      <p>{text}</p>
      {cta && <button className="btn btn-p" onClick={onCta}>{cta}</button>}
    </div>
  );
}

export default function Sites() {
  const { sites, setSites, entreprises, setEntreprises, loadRef } = useApp();
  const nav = useNavigate();
  const [siteDraft, setSiteDraft] = useState(null);
  const [entDraft, setEntDraft] = useState(null);

  const startSite = () => setSiteDraft({ nom: "", ville: "", adresse: "", criticite: "moderee" });
  const startEnt = () => setEntDraft({ nom: "", type: "exterieure", siret: "" });

  const saveSite = async () => {
    try {
      const { data } = await api.post("/sites", siteDraft);
      setSites((s) => [...s, data]);
      setSiteDraft(null);
      loadRef(); // resynchronise sites + zones (zone par défaut auto-créée)
      toast.success("Site créé — une zone principale a été ajoutée");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const saveEnt = async () => {
    try {
      const { data } = await api.post("/entreprises", entDraft);
      setEntreprises((s) => [...s, data]);
      setEntDraft(null);
      toast.success("Entreprise créée");
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <div className="page-head">
        <div><div className="crumb">Référentiel · Organisation</div><h1>Sites &amp; Entreprises</h1></div>
      </div>
      <div className="notice">Configurez votre organisation : créez d'abord vos sites, puis les entreprises extérieures qui interviennent. Vous pourrez ensuite lancer des opérations.</div>

      {/* --- Sites --- */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "18px 0 10px" }}>
        <h2 style={{ fontSize: "1.15rem" }}>Sites</h2>
        <button className="btn btn-p" onClick={startSite} data-testid="add-site-btn"><Plus size={15} />Ajouter un site</button>
      </div>

      {siteDraft && (
        <div className="card" style={{ borderLeft: "4px solid var(--sarcelle)" }} data-testid="site-editor">
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <h3 style={{ fontSize: "1rem" }}>Nouveau site</h3>
            <button className="iconbtn" onClick={() => setSiteDraft(null)}><X size={15} /></button>
          </div>
          <div className="row2">
            <div><label className="f">Nom du site</label>
              <input value={siteDraft.nom} onChange={(e) => setSiteDraft({ ...siteDraft, nom: e.target.value })} data-testid="site-nom" placeholder="Ex : Terminal céréalier Rochefort" /></div>
            <div><label className="f">Ville</label>
              <input value={siteDraft.ville} onChange={(e) => setSiteDraft({ ...siteDraft, ville: e.target.value })} data-testid="site-ville" /></div>
          </div>
          <label className="f">Adresse</label>
          <input value={siteDraft.adresse} onChange={(e) => setSiteDraft({ ...siteDraft, adresse: e.target.value })} data-testid="site-adresse" />
          <label className="f">Criticité du site</label>
          <select value={siteDraft.criticite} onChange={(e) => setSiteDraft({ ...siteDraft, criticite: e.target.value })} data-testid="site-criticite">
            {CRITICITE.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <div style={{ marginTop: 14 }}><button className="btn btn-p" onClick={saveSite} disabled={!siteDraft.nom} data-testid="site-save">Enregistrer le site</button></div>
        </div>
      )}

      {sites.length ? (
        <div className="grid3">
          {sites.map((s) => (
            <div className="card" key={s.id} data-testid={`site-card-${s.id}`}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Building size={18} color="var(--sarcelle)" />
                <h3 style={{ fontSize: "1rem" }}>{s.nom}</h3>
              </div>
              <div style={{ fontSize: ".8rem", color: "var(--text-muted)", marginTop: 8, display: "flex", alignItems: "center", gap: 5 }}>
                <MapPin size={13} />{[s.ville, s.adresse].filter(Boolean).join(" · ") || "Adresse non renseignée"}
              </div>
              <button className="btn btn-g btn-sm" style={{ marginTop: 12 }} onClick={() => nav("/zones")} data-testid={`site-zones-${s.id}`}>Gérer les zones<ArrowRight size={13} /></button>
            </div>
          ))}
        </div>
      ) : !siteDraft && (
        <EmptyState icon={Building} testid="sites-empty" title="Aucun site pour l'instant"
          text="Créez votre premier site d'intervention. Il accueillera vos zones, vos opérations et vos plans de prévention."
          cta="Créer mon premier site" onCta={startSite} />
      )}

      {/* --- Entreprises --- */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "26px 0 10px" }}>
        <h2 style={{ fontSize: "1.15rem" }}>Entreprises extérieures</h2>
        <button className="btn btn-p" onClick={startEnt} data-testid="add-entreprise-btn"><Plus size={15} />Ajouter une entreprise</button>
      </div>

      {entDraft && (
        <div className="card" style={{ borderLeft: "4px solid var(--ambre)" }} data-testid="entreprise-editor">
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <h3 style={{ fontSize: "1rem" }}>Nouvelle entreprise</h3>
            <button className="iconbtn" onClick={() => setEntDraft(null)}><X size={15} /></button>
          </div>
          <div className="row2">
            <div><label className="f">Raison sociale</label>
              <input value={entDraft.nom} onChange={(e) => setEntDraft({ ...entDraft, nom: e.target.value })} data-testid="entreprise-nom" placeholder="Ex : Maintenance Industrielle 17" /></div>
            <div><label className="f">Type</label>
              <select value={entDraft.type} onChange={(e) => setEntDraft({ ...entDraft, type: e.target.value })} data-testid="entreprise-type">
                {ENT_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select></div>
          </div>
          <label className="f">SIRET (optionnel)</label>
          <input value={entDraft.siret} onChange={(e) => setEntDraft({ ...entDraft, siret: e.target.value })} data-testid="entreprise-siret" />
          <div style={{ marginTop: 14 }}><button className="btn btn-p" onClick={saveEnt} disabled={!entDraft.nom} data-testid="entreprise-save">Enregistrer l'entreprise</button></div>
        </div>
      )}

      {entreprises.length ? (
        <div className="grid3">
          {entreprises.map((e) => (
            <div className="card" key={e.id} data-testid={`entreprise-card-${e.id}`}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Building2 size={18} color="var(--ambre-f)" />
                <h3 style={{ fontSize: "1rem" }}>{e.nom}</h3>
              </div>
              <div style={{ marginTop: 8 }}>
                <span className="tag">{ENT_TYPES.find(([v]) => v === e.type)?.[1] || e.type}</span>
              </div>
              {e.siret && <div className="mono" style={{ fontSize: ".7rem", color: "var(--text-muted)", marginTop: 6 }}>SIRET {e.siret}</div>}
            </div>
          ))}
        </div>
      ) : !entDraft && (
        <EmptyState icon={Building2} testid="entreprises-empty" title="Aucune entreprise enregistrée"
          text="Ajoutez les entreprises extérieures et sous-traitants qui interviennent sur vos sites."
          cta="Ajouter une entreprise" onCta={startEnt} />
      )}
    </div>
  );
}
